import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { initValueLead } from '../../shared/initials';
import apiServices from '../../services/service';
import { toast } from 'react-toastify';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import { fetchDataAndUpdateStore, singleRowAddUpdateInStore } from '../../redux/actions';
import LeadForm from './leadForm';
import { validationSchemaLead } from './validationSchema';
import { getGridDataByModuleKey, getUser } from '../../redux/selector';
import { dropdownPayload } from '../../shared/constants';
const CL_ICONS = {
  chevL: <path d="M15 18l-6-6 6-6" />,
  check: <path d="M20 6 9 17l-5-5" />,
  user: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  building: <path d="M3 21h18M6 21V4h9v17M9 8h3M9 12h3M9 16h3M15 21V9h4v12" />,
  campaigns: <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1ZM15 8a4 4 0 0 1 0 8M18 5a8 8 0 0 1 0 14" />,
  image: (<><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="M21 16l-5-5L5 20" /></>),
  refresh: <path d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" />,
  trash: <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6h12M10 11v6M14 11v6" />,
  checkCircle: (<><circle cx="12" cy="12" r="9" /><path d="M8.5 12l2.5 2.5 4.5-5" /></>),
};
const CLIc = ({ name, size = 16, style }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {CL_ICONS[name] || null}
  </svg>
);
const clInitials = (name) =>
  String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase();

const REQUIRED = [
  { key: 'companyName', label: 'Company Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'industryId', label: 'Industry' },
  { key: 'leadSourceId', label: 'Lead Source' },
  { key: 'sourceName', label: 'Source Name' },
  { key: 'geoScopeNodeId', label: 'GeographyNode' },
  { key: 'orgScopeNodeId', label: 'OrgUnitNode' },
];

const AddLead = (props) => {
  const gridData = useSelector(getGridDataByModuleKey('leads'));
  const handleCloseAddForm = () => {
    if (formData.from === 'view') {
      navigate(`/leads/viewLead?id=${formData.id}&pageNo=${gridData?.inputPayload?.pageNumber}`);
    } else {
      navigate('/leads');
    }
  };
  const stateData = useSelector((state) => state);
  const dispatch = useDispatch();
  let storeLoading = isLoadingByKey(
    stateData,
    'revenueTypeList',
    'leadSourceList',
    'industryList',
    'timeZone'
  );

  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useSelector(getUser);
  // Update owner in initValueLead if it is an empty string
  if (!initValueLead.owner) {
    initValueLead.owner = userInfo?.user?.display_name;
    initValueLead.ownerId = userInfo?.user?.userId;
  }
  const [loading, setLoading] = useState(storeLoading);
  const [formData, setFormData] = useState(initValueLead);
  const [errormsg, setErrorMsg] = useState({});
  const [formEdited, setFormEdited] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [imagefile, setImageFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);

  const handleUploadImage = async (imagefile) => {
    setImageFile(imagefile);
    setFormEdited(true);
  };

  // show the existing photo when editing
  useEffect(() => {
    if (formData?.id) {
      apiServices
        .getLeadImageCRM(`Lead/${formData.id}`)
        .then((url) => setPhotoPreview(url))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.id]);

  const onPhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
    handleUploadImage(file);
    e.target.value = '';
  };
  const removePhoto = () => {
    setPhotoPreview(null);
    setImageFile(null);
    setFormEdited(true);
  };

  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    let convertedValue = value;

    // For phone-related fields: Allow only numbers, +, -, parentheses, and spaces
    if (['phone', 'mobile', 'alternatePhone', 'directPhone'].includes(name)) {
      convertedValue = value.replace(/[^0-9+\-() ]/g, '');
    }

    // Employees: allow only digits and convert to int, or null if empty
    if (name === 'noOfEmployees') {
      if (value.trim() === '') {
        convertedValue = null;
      } else {
        convertedValue = value.replace(/[^0-9]/g, '');
        if (convertedValue !== '') {
          convertedValue = parseInt(convertedValue, 10);
        }
      }
    }

    // For numeric-only fields
    if (['noOfBeds'].includes(name)) {
      convertedValue = value.replace(/[^0-9]/g, '');
    }

    // For annualRevenue and writtenPremium
    if (['annualRevenue', 'writtenPremium'].includes(name)) {
      if (value === '') {
        convertedValue = null; // Send null instead of empty string
      } else {
        let sanitized = value.replace(/[^0-9.]/g, '');

        const parts = sanitized.split('.');
        if (parts.length > 2) {
          sanitized = parts[0] + '.' + parts[1];
        }

        if (parts.length === 2) {
          parts[1] = parts[1].slice(0, 2);
          sanitized = parts[0] + '.' + parts[1];
        }

        const parsed = parseFloat(sanitized);
        convertedValue = !isNaN(parsed) && /^\d+(\.\d{1,2})?$/.test(sanitized) ? parsed : sanitized;
      }
    }

    // Clear the error for this field
    setErrorMsg((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [name]: convertedValue,
    }));

    setFormEdited(true);
  };

  const handleChange = (selectedOption, name) => {
    setSelectedOptions((prevState) => ({
      ...prevState,
      [name]: selectedOption,
    }));

    handleLookupDropDownChange(selectedOption.value, selectedOption, name);
  };
  const handleLookupDropDownChange = (selectedId, selectedOption, name) => {
    const selectedValue = selectedOption.label;
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));
    if (name === 'revenueTypeId') {
      setFormData({
        ...formData,
        revenueTypeId: selectedId,
        revenueType: selectedValue,
      });

      setFormEdited(true);
    } else if (name === 'leadSourceId') {
      setFormData({
        ...formData,
        leadSourceId: selectedId,
        leadSource: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'industryId') {
      setFormData({
        ...formData,
        industryId: selectedId,
        industry: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'ownerId') {
      setFormData({
        ...formData,
        ownerId: selectedId,
        owner: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'timeZoneId') {
      setFormData({
        ...formData,
        timeZoneId: selectedId,
        timeZone: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'salutationId') {
      setFormData({
        ...formData,
        salutationId: selectedId,
        salutation: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'accessLevel') {
      setFormData({
        ...formData,
        accessLevel: selectedId,
      });
      setFormEdited(true);
    } else if (name === 'geoScopeNodeId') {
      setFormData({
        ...formData,
        geoScopeNodeId: selectedId,
        geoScopeNode: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'orgScopeNodeId') {
      setFormData({
        ...formData,
        orgScopeNodeId: selectedId,
        orgScopeNode: selectedValue,
      });
      setFormEdited(true);
    }
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target; // Use 'checked' for true/false value
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: checked, // Set the checkbox state as true/false
    }));
    setFormEdited(true);
  };

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      // Remove "https://" and "www." from relevant fields
      const sanitizedData = {
        ...formData,
        website: formData?.website?.replace(/^(https?:\/\/)?(www\.)?/, ''), // Remove protocol and www from website
        contactLinkedIn: formData?.contactLinkedIn?.replace(/^(https?:\/\/)?(www\.)?/, ''), // Remove protocol and www from contactLinkedIn
        companyLinkedIn: formData?.companyLinkedIn?.replace(/^(https?:\/\/)?(www\.)?/, ''), // Remove protocol and www from companyLinkedIn
      };

      await validationSchemaLead.validate(sanitizedData, { abortEarly: false });
      handleFormSubmit(sanitizedData);
    } catch (error) {
      setLoading(false);
      const fieldErrors = {};
      error.inner.forEach((err) => {
        fieldErrors[err.path] = err.message;
      });
      setErrorMsg(fieldErrors);
      const errorFields = Object.keys(fieldErrors);
      console.log('text', errorFields);
      if (errorFields.length > 0) {
        const firstErrorKey = errorFields[0];
        const errorRef = refs[firstErrorKey];
        console.log('errorRef', errorRef);
        if (errorRef?.current) {
          errorRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          errorRef.current.focus();
        }
      } else {
        // Submit the form!
      }

      console.error('Validation Error:', fieldErrors);
    }
  };
  const refs = {
    companyName: useRef(null),
    lastName: useRef(null),
    industryId: useRef(null),
    leadSourceId: useRef(null),
    sourceName: useRef(null),
    geoScopeNodeId: useRef(null),
    orgScopeNodeId: useRef(null),
  };
  const updateList = async (msg, id) => {
    setLoading(false);

    toast.success(msg, {
      position: 'top-right',
    });

    const columnName = 'UpdatedDateTime';
    const orderType = 'desc';
    const data = {
      pageSize: parseInt(process.env.REACT_APP_DEFAULT_PAGESIZE),
      pageNumber: 1,
      columnName,
      orderType,
      filterJson: null,
    };

    try {
      // ✅ Await the dispatch before navigation
      // await dispatch(
      //   fetchDataAndUpdateStore({ key: "leads", inputData: data })
      // );
      dispatch(
        singleRowAddUpdateInStore({
          moduleName: 'Lead',
          id: formData.type === 'add' ? id : formData.id,
          rowIndex: formData.rowIndex,
        })
      );
      dispatch(
        fetchDataAndUpdateStore({
          key: 'leadDropdown',
          inputData: dropdownPayload,
        })
      );
      if (formData.from === 'view') {
        navigate(`/leads/viewLead?id=${formData.id}&pageNo=${gridData?.inputPayload?.pageNumber}`);
      } else {
        navigate('/leads');
      }
    } catch (error) {
      console.error('Failed to fetch and update leads:', error);
      toast.error('Something went wrong while updating the list.', {
        position: 'top-right',
      });
    }
  };

  const saveUploadImage = async (id) => {
    if (imagefile && id) {
      const newFile = new File([imagefile], id.data, {
        type: imagefile.type,
      });
      let status = await apiServices.uploadLeadImageCRM(newFile, 'Lead');
    }
  };

  const editUploadImage = async (id) => {
    if (imagefile && id) {
      const newFile = new File([imagefile], id, {
        type: imagefile.type,
      });
      let status = await apiServices.uploadLeadImageCRM(newFile, 'Lead');
    }
  };

  const handleFormSubmit = (formData) => {
    if (formData.type === 'add') {
      apiServices
        .createLead(formData)
        .then((response) => {
          if (response.status === 200) {
            const id = response.data;
            saveUploadImage(response);
            updateList('Lead created successfully!', id);
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })

        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);
          toast.error('An unexpected error occurred.', {
            position: 'top-right',
          });
          setLoading(false);
        });
    } else {
      editUploadImage(formData.id);
      apiServices
        .updateLead(formData)
        .then((response) => {
          if (response.status === 200) {
            updateList('Lead updated successfully!');
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })

        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);
          toast.error('An unexpected error occurred.', {
            position: 'top-right',
          });
          setLoading(false);
        });
    }
  };
  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    let updatedFormData = {
      ...formData,
      type: location.pathname.includes('CreateNew') ? 'add' : 'edit',
    };
    if (location.pathname.includes('editLead') && location.state) {
      updatedFormData = {
        ...location.state,
        type: 'edit',
      };
    }
    setFormData(updatedFormData);
  }, [location]);

  const isEdit = formData?.type === 'edit';
  const fullName = [formData?.firstName, formData?.middleName, formData?.lastName].filter(Boolean).join(' ').trim();
  const displayName = fullName || (isEdit ? 'Lead' : 'Untitled lead');
  const idSub = [formData?.title, formData?.companyName].filter(Boolean).join(' · ');
  const filledReq = REQUIRED.filter((r) => {
    const v = formData?.[r.key];
    return v !== null && v !== undefined && String(v).trim() !== '';
  }).length;
  const pct = Math.round((filledReq / REQUIRED.length) * 100);
  const allReady = filledReq === REQUIRED.length;

  return (
    <div className="addlead_container gos-cl">
      {/* sticky header */}
      <div className="gos-cl-header">
        <button type="button" className="gos-cl-back" onClick={handleCloseAddForm} title="Back to Leads">
          <CLIc name="chevL" size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gos-cl-htitle">{isEdit ? 'Edit Lead' : 'New Lead'}</div>
          <div className="gos-cl-hsub">
            Fields marked <span className="gos-cl-reqdot">*</span> are required
          </div>
        </div>
        <button type="button" className="gos-btn gos-btn-ghost" onClick={handleCloseAddForm}>Cancel</button>
        <button
          type="button"
          className="gos-btn gos-btn-primary"
          onClick={() => handleSubmit(formData)}
          disabled={loading || (isEdit && !formEdited)}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving…
            </>
          ) : (
            <>
              <CLIc name="check" size={16} /> Save Lead
            </>
          )}
        </button>
      </div>

      <div className="gos-cl-scroll">
        <div className="gos-cl3-body">
          {/* cover */}
          <div className="gos-cl3-cover">
            <div className="gos-cl-photo-wrap">
              <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPhotoPick} />
              <div
                className={'gos-cl-photo' + (photoPreview ? ' has-img' : '')}
                onClick={photoPreview ? undefined : () => photoInputRef.current?.click()}
              >
                {photoPreview ? (
                  <>
                    <img src={photoPreview} alt="Lead" />
                    <div className="gos-cl-photo-overlay" onClick={() => photoInputRef.current?.click()}>Change</div>
                  </>
                ) : (
                  <div className="gos-cl-photo-empty">
                    <CLIc name="image" size={24} />
                    <span>Add photo</span>
                  </div>
                )}
              </div>
              {photoPreview ? (
                <div className="gos-cl-photo-actions">
                  <button type="button" className="gos-cl-photo-act" onClick={() => photoInputRef.current?.click()}>
                    <CLIc name="refresh" size={12} /> Change
                  </button>
                  <button type="button" className="gos-cl-photo-act danger" onClick={removePhoto}>
                    <CLIc name="trash" size={12} /> Remove
                  </button>
                </div>
              ) : (
                <div className="gos-cl-photo-hint">JPG or PNG · click to upload</div>
              )}
            </div>

            <div className="gos-cl3-id">
              <div className="gos-cl3-id-eyebrow">{isEdit ? 'Edit Lead' : 'New Lead'}</div>
              <div className={'gos-cl3-id-name' + (fullName ? '' : ' ph')}>{displayName}</div>
              <div className="gos-cl3-id-sub">{idSub || 'Add company and contact details below'}</div>
              <div className="gos-cl3-id-chips">
                <span className="gos-lr-chip"><CLIc name="user" size={13} /> Owner: {formData?.owner || 'Unassigned'}</span>
                {formData?.industry ? <span className="gos-lr-chip"><CLIc name="building" size={13} /> {formData.industry}</span> : null}
                {formData?.leadSource ? <span className="gos-lr-chip"><CLIc name="campaigns" size={13} /> {formData.leadSource}</span> : null}
              </div>
            </div>

            <div className={'gos-cl3-ready' + (allReady ? ' full' : '')}>
              <div className="gos-cl3-ready-top">
                <span className="gos-cl3-ready-label">Lead readiness</span>
                <span className="gos-cl3-ready-pct">{pct}%</span>
              </div>
              <div className="gos-cl3-bar"><div style={{ width: pct + '%' }} /></div>
              <div className={'gos-cl3-ready-sub' + (allReady ? ' ok' : '')}>
                {allReady ? (
                  <><CLIc name="checkCircle" size={13} /> All required fields complete</>
                ) : (
                  `${filledReq} of ${REQUIRED.length} required fields filled`
                )}
              </div>
            </div>
          </div>

          <LeadForm
            formData={formData}
            selectedOptions={selectedOptions}
            handleSubmit={handleSubmit}
            handleChangeForm={handleChangeForm}
            handleChangeCheckbox={handleChangeCheckbox}
            handleChange={handleChange}
            errormsg={errormsg}
            refs={refs}
          />
        </div>
      </div>
    </div>
  );
};

export default AddLead;
